import css from './wordQueue.module.scss';

import { useState, useCallback, memo } from 'react';

import WordPane from 'com/WordPane';
import { useSafeState } from 'core/helpers/lifeCycle';

import QueueControl from './QueueControl';
import useShiftAnimation from './useShiftAnimation';

type TProps = {
  deck: TDeck;
  words: TWord[];
  onFinish: () => unknown;
  exercise: keyof Pick<TExerciseEnum, 'wordCards' | 'wordCardsReverse'>;
  onUpdateWord: (word: TWord) => void;
};

const WordQueue: React.FC<TProps> = ({
  deck,
  words,
  exercise,
  onFinish,
  onUpdateWord,
}) => {
  const [currentWordPos, setCurrentWordPos] = useState(0);
  const { setCard1, setCard2, doShift, isAnimating } = useShiftAnimation();
  const [undo, setUndo] = useSafeState<TUndo | null>(null);

  const goNext = useCallback(() => {
    doShift()
      .then(() => {
        setCurrentWordPos(pos => {
          if (pos >= words.length - 1) {
            onFinish();
            return pos;
          }
          return pos + 1;
        });
      })
      .catch(console.error);
  }, [words, doShift]);

  // todo: add animation?
  const goPrev = useCallback(() => setCurrentWordPos(pos => pos - 1), []);

  const isLast = currentWordPos >= words.length - 1;

  const currentWord = (window.CURRENT_WORD = words[currentWordPos]);

  return (
    <div className={css.queue}>
      <QueueControl
        pos={currentWordPos}
        count={words.length}
        finish={onFinish}
        undo={undo}
        setUndo={setUndo}
        goPrev={goPrev}
        goNext={goNext}
        onUpdateWord={onUpdateWord}
        wordUid={currentWord.uid}
        deck={deck}
        exercise={exercise}
      />
      <div className={css.cardBox}>
        <div
          ref={setCard1}
          key={currentWordPos}
          className={css.firstCardWrapper}
        >
          <WordPane
            word={currentWord}
            deck={deck}
            goNext={goNext}
            isLast={isLast}
            exercise={exercise}
            setUndo={setUndo}
            disabled={isAnimating}
          />
        </div>
        {!isLast && (
          <div
            className={css.secondCard}
            ref={setCard2}
            key={`${currentWordPos + 1}-next`}
          >
            <WordPane
              word={words[currentWordPos + 1]}
              disabled={true}
              goNext={() => null}
              isLast={currentWordPos + 1 >= words.length - 1}
              deck={deck}
              exercise={exercise}
              setUndo={setUndo}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(WordQueue);
